import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Search,
  Bookmark,
  BookmarkCheck,
  Clock,
  ChevronLeft,
  Share2,
  Sparkles,
  Loader2,
  BookOpen,
} from "lucide-react";
import { LIBRARY_CATEGORIES } from "../data/library";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import { useShareSheet } from "../components/ShareSheet";
import { toast } from "sonner";

/* ============================================================
   Beauty Library — luxury editorial reading experience.
   - "For You" / "Saved" top tabs
   - Horizontal-scrolling category pills
   - Horizontal article carousels per category on the For You tab
   - Reading progress bar on article detail
   - Articles fetched live from Supabase `articles` table
   ============================================================ */

function rankArticlesForUser(articles, profile) {
  if (!profile) return articles;
  const userTags = new Set(
    [
      profile.skin_type,
      profile.skin_tone,
      profile.hair_type,
      ...(profile.concerns || []).map((c) => String(c).replace(/^(skin|hair):/i, "")),
      ...(profile.goals || []),
    ]
      .filter(Boolean)
      .map((t) => String(t).toLowerCase())
  );
  return [...articles]
    .map((a) => {
      const tags = (a.for_you_tags || []).map((t) => t.toLowerCase());
      const score = tags.reduce(
        (acc, t) =>
          acc +
          (userTags.has(t) ? 3 : 0) +
          ([...userTags].some((ut) => t.includes(ut) || ut.includes(t)) ? 1 : 0),
        0
      );
      return { ...a, _score: score };
    })
    .sort((a, b) => b._score - a._score || a.position - b.position);
}

export default function Library() {
  const { profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const share = useShareSheet();

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("for-you"); // for-you | saved
  const [cat, setCat] = useState("All");
  const [query, setQuery] = useState("");
  const [saved, setSaved] = useState(new Set());
  const [active, setActive] = useState(location.state?.openArticle || null);

  // Deep-link support: /app/library?article=<id> opens that article as soon
  // as the articles list has loaded. Used by shared share-sheet URLs.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const target = params.get("article");
    if (target && articles.length) {
      const match = articles.find((a) => String(a.id) === String(target));
      if (match) setActive(match.id);
    }
  }, [location.search, articles]);

  // Fetch articles from Supabase
  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .order("position", { ascending: true });
      if (!alive) return;
      if (error) {
        console.error("articles fetch", error);
        toast.error("Couldn't load library");
      }
      setArticles(data || []);
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Fetch user's saved article ids
  useEffect(() => {
    if (!profile?.id) return;
    supabase
      .from("saved_content")
      .select("content_id")
      .eq("user_id", profile.id)
      .eq("content_type", "article")
      .then(({ data }) =>
        setSaved(new Set((data || []).map((r) => r.content_id)))
      );
  }, [profile?.id]);

  useEffect(() => {
    if (location.state?.openArticle) {
      setActive(location.state.openArticle);
      // Clear the deep-link state so a fresh Library tab visit lands on the list view.
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const toggleSave = async (id) => {
    if (!profile?.id) {
      toast.error("Sign in to save articles");
      return;
    }
    const isSaved = saved.has(id);
    const next = new Set(saved);
    if (isSaved) next.delete(id);
    else next.add(id);
    setSaved(next);

    if (isSaved) {
      const { error } = await supabase
        .from("saved_content")
        .delete()
        .eq("user_id", profile.id)
        .eq("content_id", id)
        .eq("content_type", "article");
      if (error) {
        setSaved((s) => new Set([...s, id]));
        toast.error("Couldn't unsave");
        return;
      }
      toast("Removed from your library");
    } else {
      const { error } = await supabase
        .from("saved_content")
        .insert({ user_id: profile.id, content_id: id, content_type: "article" });
      if (error) {
        setSaved((s) => {
          const r = new Set(s);
          r.delete(id);
          return r;
        });
        toast.error("Couldn't save");
        return;
      }
      toast.success("Saved to your library");
    }
  };

  // ------------- Search filter -------------
  const searched = useMemo(() => {
    if (!query.trim()) return articles;
    const q = query.trim().toLowerCase();
    return articles.filter(
      (a) =>
        a.title.toLowerCase().includes(q) ||
        (a.excerpt || "").toLowerCase().includes(q) ||
        (a.category || "").toLowerCase().includes(q)
    );
  }, [articles, query]);

  // ------------- For You ranking -------------
  const personalized = useMemo(
    () => rankArticlesForUser(searched, profile),
    [searched, profile]
  );

  // Saved list
  const savedList = useMemo(
    () => articles.filter((a) => saved.has(a.id)),
    [articles, saved]
  );

  // ------------- Article detail view -------------
  if (active) {
    const article = articles.find((a) => a.id === active);
    if (!article) {
      return (
        <div className="px-5 pt-6">
          <button
            onClick={() => setActive(null)}
            className="text-sm text-cocoa/60"
          >
            ← Back
          </button>
          <p className="mt-4 text-cocoa/60">Article not found.</p>
        </div>
      );
    }
    return (
      <ArticleReader
        article={article}
        saved={saved.has(active)}
        onBack={() => setActive(null)}
        onSave={() => toggleSave(active)}
        onShare={() =>
          share.open({
            type: "article",
            payload: { title: article.title, id: article.id },
          })
        }
      />
    );
  }

  // Category filter
  const byCat = (list) =>
    cat === "All" ? list : list.filter((a) => a.category === cat);

  return (
    <div className="-mt-px" data-testid="library-page">
      {/* HERO HEADER */}
      <div
        className="relative pt-6 px-5 pb-5"
        style={{
          background: "linear-gradient(180deg, #C9748A 0%, #B8657A 100%)",
        }}
      >
        <p className="text-[10px] uppercase tracking-[0.28em] text-white/85 font-medium mb-1">
          Knowledge · Expert-curated
        </p>
        <h1 className="font-serif text-white text-3xl leading-tight">
          Beauty Library
        </h1>
        <p className="text-white/85 text-sm font-light mt-1">
          Editorial guides written by experts — for every skin tone and texture.
        </p>

        {/* Search */}
        <div
          className="bg-white rounded-full mt-4 px-4 py-2.5 flex items-center gap-2"
          data-testid="library-search"
        >
          <Search size={14} className="text-cocoa/40 shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles, ingredients, textures..."
            data-testid="library-search-input"
            className="flex-1 bg-transparent text-sm focus:outline-none placeholder:text-cocoa/40 text-cocoa"
          />
        </div>
      </div>

      <div className="px-5 pt-4 pb-24">
        {/* FOR YOU / SAVED TABS */}
        <div className="flex gap-2 mb-4" data-testid="library-tabs">
          <button
            onClick={() => setTab("for-you")}
            data-testid="library-tab-for-you"
            className={`flex-1 rounded-full py-2 text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
              tab === "for-you"
                ? "bg-rose text-white shadow-rose"
                : "bg-white text-cocoa/60 border border-[#f0e8e4]"
            }`}
          >
            <Sparkles size={12} className={tab === "for-you" ? "fill-white/30" : ""} />
            For You
          </button>
          <button
            onClick={() => setTab("saved")}
            data-testid="library-tab-saved"
            className={`flex-1 rounded-full py-2 text-sm font-medium transition-all flex items-center justify-center gap-1.5 ${
              tab === "saved"
                ? "bg-rose text-white shadow-rose"
                : "bg-white text-cocoa/60 border border-[#f0e8e4]"
            }`}
          >
            <Bookmark
              size={12}
              className={tab === "saved" ? "fill-white" : ""}
            />
            Saved
            {saved.size > 0 && (
              <span
                className={`text-[10px] rounded-full px-1.5 py-0.5 ${
                  tab === "saved" ? "bg-white/25" : "bg-rose/10 text-rose"
                }`}
              >
                {saved.size}
              </span>
            )}
          </button>
        </div>

        {/* CATEGORY PILLS (horizontal scroll) */}
        <div
          className="flex gap-2 mb-5 overflow-x-auto no-scrollbar -mx-1 px-1"
          data-testid="library-categories"
        >
          {LIBRARY_CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              data-testid={`library-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-4 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                cat === c
                  ? "bg-cocoa text-white"
                  : "bg-white text-cocoa/60 border border-[#f0e8e4]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {loading ? (
          <div
            className="flex items-center justify-center py-16 text-cocoa/50"
            data-testid="library-loading"
          >
            <Loader2 className="animate-spin mr-2" size={18} /> Loading library…
          </div>
        ) : tab === "for-you" ? (
          <ForYouView
            articles={byCat(personalized)}
            allRanked={personalized}
            cat={cat}
            saved={saved}
            onOpen={setActive}
            onSave={toggleSave}
          />
        ) : (
          <SavedView
            articles={byCat(savedList)}
            saved={saved}
            onOpen={setActive}
            onSave={toggleSave}
          />
        )}
      </div>
    </div>
  );
}

/* ============================================================
   For You — featured editor's pick + horizontal carousels by category
   ============================================================ */
function ForYouView({ articles, allRanked, cat, saved, onOpen, onSave }) {
  // Featured = top-ranked article on the active filter
  const featured = articles[0];
  const rest = articles.slice(1);

  // Group rest by category for horizontal rows (only when cat === "All")
  const byCategory = useMemo(() => {
    if (cat !== "All") return null;
    const groups = {};
    for (const a of rest) {
      groups[a.category] = groups[a.category] || [];
      groups[a.category].push(a);
    }
    return groups;
  }, [rest, cat]);

  if (!articles.length) {
    return (
      <div
        className="rounded-3xl bg-white border border-[#f0e8e4] p-10 text-center"
        data-testid="library-empty"
      >
        <BookOpen className="mx-auto mb-3 text-rose/70" size={28} />
        <p className="font-serif text-xl text-cocoa">Nothing to read yet</p>
        <p className="text-sm text-cocoa/55 font-light mt-1">
          Try another category — your editorial picks are loading.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="library-foryou">
      {featured && (
        <FeaturedCard
          article={featured}
          saved={saved.has(featured.id)}
          onOpen={() => onOpen(featured.id)}
          onSave={() => onSave(featured.id)}
        />
      )}

      {cat === "All" ? (
        // Horizontal carousels by category
        Object.entries(byCategory).map(([category, list]) =>
          list.length === 0 ? null : (
            <CategoryRow
              key={category}
              category={category}
              list={list}
              saved={saved}
              onOpen={onOpen}
              onSave={onSave}
            />
          )
        )
      ) : (
        // Within a filtered category, show a 2-col grid of remaining
        <div>
          <h2 className="font-serif text-2xl text-cocoa mb-3">More in {cat}</h2>
          <div className="grid grid-cols-2 gap-3">
            {rest.map((a) => (
              <ArticleCard
                key={a.id}
                article={a}
                saved={saved.has(a.id)}
                onOpen={() => onOpen(a.id)}
                onSave={() => onSave(a.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   Saved — list of bookmarked articles
   ============================================================ */
function SavedView({ articles, saved, onOpen, onSave }) {
  if (!articles.length) {
    return (
      <div
        className="rounded-3xl bg-white border border-[#f0e8e4] p-10 text-center"
        data-testid="library-saved-empty"
      >
        <Bookmark className="mx-auto mb-3 text-rose/70" size={28} />
        <p className="font-serif text-xl text-cocoa">Your library is empty</p>
        <p className="text-sm text-cocoa/55 font-light mt-1">
          Tap the bookmark on any article to keep it here for later.
        </p>
      </div>
    );
  }
  return (
    <div data-testid="library-saved-list">
      <h2 className="font-serif text-2xl text-cocoa mb-3">Your Saved Reads</h2>
      <div className="grid grid-cols-1 gap-3">
        {articles.map((a) => (
          <ArticleRowCard
            key={a.id}
            article={a}
            saved={saved.has(a.id)}
            onOpen={() => onOpen(a.id)}
            onSave={() => onSave(a.id)}
          />
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   Featured (Editor's Pick) card
   ============================================================ */
function FeaturedCard({ article, saved, onOpen, onSave }) {
  return (
    <motion.button
      onClick={onOpen}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="library-featured"
      className="block w-full text-left rounded-3xl p-6 relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #C9748A 0%, #C9975A 100%)",
      }}
    >
      <span
        aria-hidden
        className="absolute -right-4 -bottom-4 text-[110px] opacity-25 select-none pointer-events-none"
      >
        ✨
      </span>
      <div className="relative">
        <p className="text-[10px] uppercase tracking-[0.28em] text-white font-medium mb-2 inline-flex items-center gap-1.5">
          <Sparkles size={11} /> Editor&apos;s Pick
        </p>
        <h2 className="font-serif text-white text-2xl leading-tight mb-2">
          {article.title}
        </h2>
        <p className="text-white/85 text-sm font-light mb-3 line-clamp-2">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs text-white/85 font-light">
            <Clock size={11} /> {article.read_time} ·{" "}
            {article.category}
          </span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onSave();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onSave();
              }
            }}
            data-testid={`save-article-${article.id}`}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/15 backdrop-blur-sm cursor-pointer"
            aria-label={saved ? "Unsave article" : "Save article"}
          >
            {saved ? (
              <BookmarkCheck size={16} className="text-white fill-white" />
            ) : (
              <Bookmark size={16} className="text-white" />
            )}
          </span>
        </div>
      </div>
    </motion.button>
  );
}

/* ============================================================
   Horizontal carousel row
   ============================================================ */
function CategoryRow({ category, list, saved, onOpen, onSave }) {
  return (
    <section data-testid={`library-row-${category.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-end justify-between mb-2">
        <h2 className="font-serif text-2xl text-cocoa">{category}</h2>
        <span className="text-[10px] uppercase tracking-[0.2em] text-gold font-medium">
          {list.length} {list.length === 1 ? "read" : "reads"}
        </span>
      </div>
      <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-5 px-5 pb-1">
        {list.map((a) => (
          <ArticleCardHorizontal
            key={a.id}
            article={a}
            saved={saved.has(a.id)}
            onOpen={() => onOpen(a.id)}
            onSave={() => onSave(a.id)}
          />
        ))}
      </div>
    </section>
  );
}

/* ============================================================
   Horizontal card (used inside CategoryRow)
   ============================================================ */
function ArticleCardHorizontal({ article, saved, onOpen, onSave }) {
  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      onClick={onOpen}
      data-testid={`article-${article.id}`}
      className="shrink-0 w-[230px] bg-white rounded-2xl p-4 shadow-card cursor-pointer flex flex-col"
    >
      <div
        className="h-24 rounded-xl mb-3 relative overflow-hidden"
        style={{
          background:
            article.category === "Skincare"
              ? "linear-gradient(135deg, #F4D7DE 0%, #C9748A 100%)"
              : article.category === "Haircare"
              ? "linear-gradient(135deg, #EAD4B8 0%, #C9975A 100%)"
              : article.category === "Ingredients"
              ? "linear-gradient(135deg, #E8DECF 0%, #B8956F 100%)"
              : article.category === "Wellness"
              ? "linear-gradient(135deg, #DCE5D8 0%, #8DA785 100%)"
              : "linear-gradient(135deg, #F4DCC3 0%, #D6A878 100%)",
        }}
      >
        <span className="absolute top-2 left-2 text-[9px] uppercase tracking-[0.2em] text-white font-medium bg-cocoa/30 backdrop-blur-sm rounded-full px-2 py-0.5">
          {article.category}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSave();
          }}
          data-testid={`save-article-${article.id}`}
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center"
        >
          {saved ? (
            <BookmarkCheck size={13} className="text-white fill-white" />
          ) : (
            <Bookmark size={13} className="text-white" />
          )}
        </button>
      </div>
      <h3 className="font-serif text-base text-cocoa leading-snug mb-1 line-clamp-2">
        {article.title}
      </h3>
      <p className="text-xs text-cocoa/55 font-light line-clamp-2 mb-2 flex-1">
        {article.excerpt}
      </p>
      <span className="text-[11px] text-cocoa/55 inline-flex items-center gap-1">
        <Clock size={10} /> {article.read_time}
      </span>
    </motion.div>
  );
}

/* ============================================================
   Grid card (for category-filtered view)
   ============================================================ */
function ArticleCard({ article, saved, onOpen, onSave }) {
  return (
    <motion.div
      whileTap={{ scale: 0.98 }}
      onClick={onOpen}
      data-testid={`article-${article.id}`}
      className="bg-white rounded-2xl p-4 shadow-card cursor-pointer"
    >
      <p className="text-[9px] uppercase tracking-[0.2em] text-gold font-medium mb-1">
        {article.category}
      </p>
      <h3 className="font-serif text-base text-cocoa leading-snug mb-1.5 line-clamp-3">
        {article.title}
      </h3>
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] text-cocoa/55 inline-flex items-center gap-1">
          <Clock size={10} /> {article.read_time}
        </span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onSave();
          }}
          data-testid={`save-article-${article.id}`}
        >
          {saved ? (
            <BookmarkCheck size={14} className="text-rose fill-rose" />
          ) : (
            <Bookmark size={14} className="text-cocoa/40" />
          )}
        </button>
      </div>
    </motion.div>
  );
}

/* ============================================================
   Row card (for Saved tab)
   ============================================================ */
function ArticleRowCard({ article, saved, onOpen, onSave }) {
  return (
    <motion.div
      whileTap={{ scale: 0.99 }}
      onClick={onOpen}
      data-testid={`article-${article.id}`}
      className="bg-white rounded-2xl p-4 shadow-card cursor-pointer flex items-start gap-3"
    >
      <div
        className="w-16 h-16 rounded-xl shrink-0"
        style={{
          background:
            article.category === "Skincare"
              ? "linear-gradient(135deg, #F4D7DE 0%, #C9748A 100%)"
              : article.category === "Haircare"
              ? "linear-gradient(135deg, #EAD4B8 0%, #C9975A 100%)"
              : article.category === "Ingredients"
              ? "linear-gradient(135deg, #E8DECF 0%, #B8956F 100%)"
              : article.category === "Wellness"
              ? "linear-gradient(135deg, #DCE5D8 0%, #8DA785 100%)"
              : "linear-gradient(135deg, #F4DCC3 0%, #D6A878 100%)",
        }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-[9px] uppercase tracking-[0.22em] text-gold font-medium mb-0.5">
          {article.category}
        </p>
        <h3 className="font-serif text-base text-cocoa leading-snug mb-1 line-clamp-2">
          {article.title}
        </h3>
        <span className="text-[11px] text-cocoa/55 inline-flex items-center gap-1">
          <Clock size={10} /> {article.read_time}
        </span>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSave();
        }}
        data-testid={`save-article-${article.id}`}
        className="p-1"
      >
        {saved ? (
          <BookmarkCheck size={16} className="text-rose fill-rose" />
        ) : (
          <Bookmark size={16} className="text-cocoa/40" />
        )}
      </button>
    </motion.div>
  );
}

/* ============================================================
   Article reader — full screen with sticky reading progress bar
   ============================================================ */
function ArticleReader({ article, saved, onBack, onSave, onShare }) {
  const scrollRef = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = window.scrollY;
      const max = doc.scrollHeight - window.innerHeight;
      const pct = max <= 0 ? 0 : Math.min(100, Math.max(0, (scrolled / max) * 100));
      setProgress(pct);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [article.id]);

  // Scroll to top when an article opens
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [article.id]);

  return (
    <div className="-mt-px pb-28" data-testid="library-article" ref={scrollRef}>
      {/* Sticky top bar with progress */}
      <div
        className="sticky top-0 z-20 bg-cream/95 backdrop-blur-xl border-b border-[#f0e8e4]"
        data-testid="article-progress-wrapper"
      >
        <div className="px-5 pt-4 pb-3 flex items-center justify-between">
          <button
            onClick={onBack}
            data-testid="library-back"
            className="flex items-center gap-1 text-sm text-cocoa/70 font-medium"
          >
            <ChevronLeft size={18} /> Library
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={onSave}
              data-testid={`save-article-${article.id}`}
              className="w-9 h-9 rounded-full bg-white border border-[#f0e8e4] flex items-center justify-center"
            >
              {saved ? (
                <BookmarkCheck size={15} className="text-rose fill-rose" />
              ) : (
                <Bookmark size={15} className="text-cocoa/55" />
              )}
            </button>
            <button
              onClick={onShare}
              data-testid="article-share-btn"
              className="w-9 h-9 rounded-full bg-white border border-[#f0e8e4] flex items-center justify-center"
            >
              <Share2 size={14} className="text-rose" />
            </button>
          </div>
        </div>
        {/* Progress track */}
        <div className="h-[3px] bg-[#f0e8e4] w-full relative" data-testid="article-progress">
          <motion.div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-rose to-gold"
            style={{ width: `${progress}%` }}
            data-testid="article-progress-fill"
          />
        </div>
      </div>

      {/* Hero/title */}
      <div className="px-5 pt-6">
        <p className="text-[10px] uppercase tracking-[0.28em] text-gold font-medium mb-2">
          {article.category}
        </p>
        <h1 className="font-serif text-[34px] leading-[1.1] text-cocoa mb-3">
          {article.title}
        </h1>
        <div className="flex items-center gap-3 text-xs text-cocoa/55 mb-6">
          <span className="inline-flex items-center gap-1">
            <Clock size={11} /> {article.read_time} read
          </span>
          {article.word_count ? (
            <>
              <span>·</span>
              <span>{article.word_count.toLocaleString()} words</span>
            </>
          ) : null}
        </div>

        {/* Cover graphic */}
        <div
          className="h-40 rounded-2xl mb-6 relative overflow-hidden"
          style={{
            background:
              article.category === "Skincare"
                ? "linear-gradient(135deg, #F4D7DE 0%, #C9748A 100%)"
                : article.category === "Haircare"
                ? "linear-gradient(135deg, #EAD4B8 0%, #C9975A 100%)"
                : article.category === "Ingredients"
                ? "linear-gradient(135deg, #E8DECF 0%, #B8956F 100%)"
                : article.category === "Wellness"
                ? "linear-gradient(135deg, #DCE5D8 0%, #8DA785 100%)"
                : "linear-gradient(135deg, #F4DCC3 0%, #D6A878 100%)",
          }}
        >
          <span className="absolute right-3 bottom-3 text-6xl opacity-30 select-none">
            {article.category === "Skincare"
              ? "✨"
              : article.category === "Haircare"
              ? "💆🏽‍♀️"
              : article.category === "Ingredients"
              ? "🧪"
              : article.category === "Wellness"
              ? "🌿"
              : "🍯"}
          </span>
        </div>

        {/* Body */}
        <ArticleBody body={article.body} />

        {/* Footer save/share */}
        <div className="mt-10 rounded-3xl bg-white border border-[#f0e8e4] p-5 flex items-center justify-between">
          <div>
            <p className="font-serif text-lg text-cocoa">Enjoyed this read?</p>
            <p className="text-xs text-cocoa/55 font-light">
              Save it for later or share with a friend.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onSave}
              data-testid={`save-article-footer-${article.id}`}
              className="w-10 h-10 rounded-full bg-rose/10 flex items-center justify-center"
            >
              {saved ? (
                <BookmarkCheck size={16} className="text-rose fill-rose" />
              ) : (
                <Bookmark size={16} className="text-rose" />
              )}
            </button>
            <button
              onClick={onShare}
              className="w-10 h-10 rounded-full bg-rose flex items-center justify-center"
            >
              <Share2 size={16} className="text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   Renders the markdown-ish article body into pretty paragraphs.
   Supports **bold** and double-newline paragraphs.
   ============================================================ */
function ArticleBody({ body }) {
  if (!body) return null;
  const paragraphs = body.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className="space-y-5 text-[15px] font-light leading-[1.75] text-cocoa">
      {paragraphs.map((p, i) => {
        // detect "lead-in bold" paragraphs (first line is **Header**) and render as section header
        const headerMatch = p.match(/^\*\*([^*]+)\*\*\s*(?::|—|–)?\s*(.*)$/s);
        if (headerMatch && headerMatch[1].length < 80) {
          const heading = headerMatch[1].trim();
          const rest = headerMatch[2].trim();
          return (
            <div key={i} className="pt-2">
              <p className="font-serif text-cocoa text-[19px] leading-tight mb-2">
                {heading}
              </p>
              {rest && (
                <p
                  className="text-cocoa/85"
                  dangerouslySetInnerHTML={{ __html: formatInline(rest) }}
                />
              )}
            </div>
          );
        }
        return (
          <p
            key={i}
            className="text-cocoa/85"
            dangerouslySetInnerHTML={{ __html: formatInline(p) }}
          />
        );
      })}
    </div>
  );
}

function formatInline(s) {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-cocoa font-semibold">$1</strong>')
    .replace(/\n/g, "<br/>");
}
